// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.

export const TECHNICAL_POLICY_COLUMNS = {
  Gisement: (attributes = [], isDisabled = false, attributeValues = []) => [
    {
      field: 'Attribut',
      label: 'genericcomponent.technicalPolicy.assetClusterTable.attribute',
      inputType: 'select',
      options: attributes,
      required: true,
    },
    {
      field: 'Operateur',
      label: 'genericcomponent.technicalPolicy.assetClusterTable.operator',
      inputType: 'select',
      options: [
        { label: 'IN', value: 'IN' },
        { label: '<', value: 'LT' },
        { label: '≤', value: 'LEQ' },
        { label: '>', value: 'GT' },
        { label: '≥', value: 'GEQ' },
      ],
      disabled: isDisabled,
      defaultValue: 'IN',
      required: true,
    },
    {
      field: 'Valeurs',
      label: 'genericcomponent.technicalPolicy.assetClusterTable.value',
      inputType: (row) => (row?.Operateur === 'IN' ? 'autocomplete' : 'number'),
      options: [],
      getOptions: attributeValues,
      required: true,
      autocompleteProps: {
        freeSolo: true,
        id: 'gisement-valeurs-autocomplete',
        checkbox: false,
        disableCloseOnSelect: true,
      },
    },
    {
      field: 'id',
      isInvisible: true,
    },
  ],
  NiveauDePriorisation: () => [
    {
      field: 'id',
      isInvisible: true,
    },
    {
      field: 'Identifiant',
      label: 'genericcomponent.technicalPolicy.prioritizationDialog.level',
      required: true,
    },
    {
      field: 'RangPriorisation',
      label: 'genericcomponent.technicalPolicy.prioritizationDialog.prioritizationRank',
      inputType: 'number',
      required: true,
    },
    {
      field: 'SchemaPriorisation',
      label: 'genericcomponent.technicalPolicy.prioritizationDialog.prioritizationFramework',
      inputType: 'select',
      options: ['RiskBased', 'ProbabilityBased', 'ConsequenceBased', 'OpportunityBased'],
      required: true,
    },
  ],
  ReglesGestion: () => [
    {
      field: 'Identifiant',
    },
    {
      field: 'TypePriorisation',
    },
    {
      field: 'NiveauDePriorisation',
    },
    {
      field: 'AnneeDebut',
    },
    {
      field: 'AnneeFin',
    },
    {
      field: 'CategoriesBudgetaires',
    },
    {
      field: 'SequenceActions1',
    },
    {
      field: 'SequenceActions2',
    },
    {
      field: 'NbIterationSequenceActions1',
    },
    { field: 'ReevaluationAnnuelleCriteres' },
    { field: 'Desactiver' },
    { field: 'FormuleCritere' },
  ],
  CriteresDeclenchement: (
    isOperateurDisabled = () => false,
    isValeurReferenceDisabled = () => false,
    isValid = () => true,
    attributeValues = [],
    family = []
  ) => [
    {
      field: 'id',
      isInvisible: true,
    },
    {
      field: 'RegleGestion',
      isInvisible: true,
    },
    {
      field: 'Identifiant',
      label: 'genericcomponent.technicalPolicy.managementRules.triggerCriteria.identifier',
      inputType: 'text',
      // This validation just show an error message but do not prevent user from validating the row
      valid: isValid,
      notValidError: 'genericcomponent.common.validationErrors.identifierAlreadyExists',
      required: true,
      getDefaultValue: (existingRows) => {
        const visibleCriteriaCount = existingRows ? existingRows.length : 0;
        return `C${visibleCriteriaCount + 1}`;
      },
    },
    {
      field: 'FamillesEquipements',
      label: 'genericcomponent.technicalPolicy.managementRules.triggerCriteria.equipmentFamilies',
      inputType: 'multiSelect',
      options: family,
    },
    {
      field: 'SeuilValidation',
      label: 'genericcomponent.technicalPolicy.managementRules.triggerCriteria.validationThreshold',
      info: ' (%), 0% correspond à au moins un actif',
      inputType: 'number',
      min: 0,
      max: 1,
      step: 0.01,
      required: true,
      defaultValue: 0,
    },
    {
      field: 'AttributDeclencheur',
      label: 'genericcomponent.technicalPolicy.managementRules.triggerCriteria.triggerAttribute',
      inputType: 'nestedSelect',
      defaultValue: 'Failure',
      options: [
        { label: 'Failure', value: 'Failure' },
        { label: 'Operating', value: 'Operating' },
        { label: 'QuasiFailure', value: 'QuasiFailure' },
        { label: 'Age', value: 'Age' },
        { label: 'AgeDuringFirstLifecycle', value: 'AgeDuringFirstLifecycle' },
        { label: 'AgeAfterFirstLifecycle', value: 'AgeAfterFirstLifecycle' },
        { label: 'State', value: 'State' },
        { label: 'Downtime', value: 'Downtime' },
        { label: 'Quantity', value: 'Quantity' },
        { label: 'CustomDataNumber.*', value: 'CustomDataNumber.*' },
        { label: 'CustomDataString.*', value: 'CustomDataString.*' },
        { label: 'InspectionDowntime', value: 'InspectionDowntime' },
        { label: 'AllPredeterminedProjectsDone', value: 'AllPredeterminedProjectsDone' },
        { label: 'CorporateCostOfFailure', value: 'CorporateCostOfFailure' },
        { label: 'SocietalCostOfFailure', value: 'SocietalCostOfFailure' },
        { label: 'CostOfFailure', value: 'CostOfFailure' },
        { label: 'SumOfCorporateCostsOfFailure', value: 'SumOfCorporateCostsOfFailure' },
        { label: 'SumOfSocietalCostsOfFailure', value: 'SumOfSocietalCostsOfFailure' },
        { label: 'SumOfCostsOfFailure', value: 'SumOfCostsOfFailure' },
        { label: 'ConsequenceQuantity.*', value: 'ConsequenceQuantity.*' },
        { label: 'SumOfConsequenceQuantities.*', value: 'SumOfConsequenceQuantities.*' },
        { label: 'PriorityScore', value: 'PriorityScore' },
        { label: 'FinancialValue', value: 'FinancialValue' },
        { label: 'EnvironmentAgingRate', value: 'EnvironmentAgingRate' },
      ],
      required: true,
    },
    {
      field: 'Operateur',
      label: 'genericcomponent.technicalPolicy.managementRules.triggerCriteria.operator',
      inputType: 'select',
      options: [
        { label: '<', value: 'LT' },
        { label: '≤', value: 'LEQ' },
        { label: '>', value: 'GT' },
        { label: '≥', value: 'GEQ' },
      ],
      // Disable if AttributDeclencheur values force to not have operator defined
      disabled: isOperateurDisabled,
      clearIfDisabled: true,
    },
    {
      field: 'ValeurReference',
      label: 'genericcomponent.technicalPolicy.managementRules.triggerCriteria.referenceValue',
      options: [
        { label: 'MinimalAge', value: 'MinimalAge' },
        { label: 'MaximalAge', value: 'MaximalAge' },
        { label: 'CustomDataNumber', value: 'CustomDataNumber' },
        { label: 'ScalarValue', value: 'ScalarValue' },
      ],
      // Disable if AttributDeclencheur values force to not have reference value defined
      disabled: isValeurReferenceDisabled,
      clearIfDisabled: true,
      inputType: (row) => (row?.AttributDeclencheur.startsWith('CustomDataString.') ? 'autocomplete' : 'select'),
      getOptions: attributeValues,
      autocompleteProps: {
        freeSolo: true,
        id: 'criteres-declenchement-valeur-reference-autocomplete',
        checkbox: false,
        disableCloseOnSelect: true,
      },
    },
  ],
  SequencesActions: (
    openCoutsEtDureesModal = () => {},
    openTachesEnInterventionModal = () => {},
    openImpactsModal = () => {},
    categoriesBudgetaires = [],
    tachesEnIntervention = [],
    isDisabled = () => false,
    isValid = () => true
  ) => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'SequenceActions', isInvisible: true },
    {
      field: 'IdentifiantAction',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.action',
      size: 'medium',
      sx: { width: '150px' },
      disabled: isDisabled,
      valid: isValid,
      required: true,
    },
    {
      field: 'RangExecution',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.executionOrder',
      inputType: 'number',
      min: 1,
      step: 1,
      size: 'small',
      getDefaultValue: (existingRows) => existingRows?.length + 1 || 1,
      required: true,
    },
    {
      field: 'CategoriesBudgetaires',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.budgetCategories',
      inputType: 'autocomplete',
      options: categoriesBudgetaires,
      size: 'medium',
      sx: { width: '200px' },
      autocompleteProps: {
        freeSolo: false,
        disableCloseOnSelect: true,
        placeholder: 'Rechercher...',
        checkbox: true,
        isSearchable: true,
        id: 'action-budget-categories-autocomplete',
      },
      info: 'Ce champ permet de rechercher une valeur grâce à une saisie assistée',
    },
    {
      field: 'NiveauDefinitionCouts',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.costDefinitionLevel',
      inputType: 'select',
      size: 'small',
      options: [
        { label: 'Action', value: 'Action' },
        { label: 'SubAction', value: 'SubAction' },
      ],
      defaultValue: 'Action',
      required: true,
    },
    {
      field: 'coutEtDurees',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.costsAndDurations',
      inputType: 'button',
      buttonLabel: 'genericcomponent.common.viewEdit',
      onClick: openCoutsEtDureesModal,
      size: 'small',
    },
    {
      field: 'impacts',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.impacts',
      inputType: 'button',
      buttonLabel: 'genericcomponent.common.viewEdit',
      onClick: openImpactsModal,
      size: 'small',
    },
    {
      field: 'CoefficientModificationCouts',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.costModificationCoefficients',
      size: 'small',
    },
    {
      field: 'CoefficientModificationDuree',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.durationModificationCoefficients',
      size: 'small',
    },
    {
      field: 'ActionLocale',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.localAction',
      inputType: 'checkbox',
      size: 'small',
    },
    {
      field: 'ConsignationRequise',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.outageRequired',
      inputType: 'checkbox',
      size: 'small',
    },
    {
      field: 'CapitalisationCharges',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.chargeCapitalization',
      inputType: 'checkbox',
      size: 'small',
    },
    {
      field: 'TachesEnIntervention',
      label: 'genericcomponent.technicalPolicy.actionSequencesTable.tasksInIntervention',
      inputType: 'autocomplete',
      withButton: true,
      buttonLabel: 'genericcomponent.common.view',
      onClick: openTachesEnInterventionModal,
      options: tachesEnIntervention,
      size: 'small',
      autocompleteProps: {
        freeSolo: false,
        disableCloseOnSelect: true,
        placeholder: 'Rechercher...',
        checkbox: true,
        isSearchable: true,
        id: 'taches-intervention-autocomplete',
      },
      info: 'Ce champ permet de rechercher une valeur grâce à une saisie assistée',
    },
  ],
  CoutsEtDureesActions: (costLevel = [], family = [], niveauDefinitionCout = 'Action') => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'IdentifiantAction', isInvisible: true },
    {
      field: 'FamilleEquipement',
      label: 'genericcomponent.technicalPolicy.costsAndDurationsTable.equipmentFamily',
      inputType: 'text',
      options: [{ label: 'Aucune', value: '' }, ...(family ?? [])],
      isInvisible: niveauDefinitionCout === 'Action',
      disabled: true,
      required: true,
      info:
        'This field must be left empty if costs are defined at action level (Cost Definition Level = Action), ' +
        'and must be filled if costs are defined at sub-action level (Cost Definition Level = SubAction)',
    },
    {
      field: 'NiveauCouts',
      label: 'genericcomponent.technicalPolicy.costsAndDurationsTable.costLevel',
      inputType: 'text',
      options: costLevel,
      disabled: true, // This field is intentionally disabled because it is not meant to be edited by the user.
      required: true,
    },
    {
      field: 'TypeCout',
      label: 'genericcomponent.technicalPolicy.costsAndDurationsTable.costType',
      inputType: 'select',
      options: ['Strict', 'Complexe'],
      required: true,
    },
    {
      field: 'CoutUnitaire',
      label: 'genericcomponent.technicalPolicy.costsAndDurationsTable.unitCost',
      required: true,
    },
    {
      field: 'CoutFixe',
      label: 'genericcomponent.technicalPolicy.costsAndDurationsTable.fixedCost',
      required: true,
    },
    {
      field: 'DureeUnitaire',
      label: 'genericcomponent.technicalPolicy.costsAndDurationsTable.unitDuration',
      required: true,
    },
    {
      field: 'DureeFixe',
      label: 'genericcomponent.technicalPolicy.costsAndDurationsTable.fixedDuration',
      required: true,
    },
  ],
  TachesEnIntervention: (tachesEnIntervention = []) => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'IdentifiantAction', isInvisible: true },
    {
      field: 'Identifiant',
      label: 'genericcomponent.technicalPolicy.tasksInInterventionTable.taskInIntervention',
      inputType: 'text',
      options: tachesEnIntervention ?? [],
      disabled: true,
    },
    {
      field: 'Competence',
      label: 'genericcomponent.technicalPolicy.tasksInInterventionTable.competence',
      inputType: 'text',
    },
    {
      field: 'DureeRelative',
      label: 'genericcomponent.technicalPolicy.tasksInInterventionTable.relativeDuration',
      inputType: 'number',
    },
    {
      field: 'ETPRequis',
      label: 'genericcomponent.technicalPolicy.tasksInInterventionTable.requiredFTE',
      inputType: 'number',
    },
  ],
  Impacts: (family = []) => [
    {
      field: 'id',
      isInvisible: true,
    },
    { field: 'IdentifiantAction', isInvisible: true },
    {
      field: 'FamilleEquipementImpactee',
      label: 'genericcomponent.technicalPolicy.impactsTable.equipmentFamily',
      inputType: 'text',
      options: family,
      disabled: true, // This field is intentionally disabled because it is not meant to be edited by the user.
      required: true,
    },
    {
      field: 'Type',
      label: 'genericcomponent.technicalPolicy.impactsTable.type',
      inputType: 'select',
      options: [
        { label: 'NoAction', value: 'NoAction' },
        { label: 'Repairs', value: 'Repairs' },
        { label: 'Replacement', value: 'Replacement' },
        { label: 'Inspection', value: 'Inspection' },
        { label: 'Activation', value: 'Activation' },
        { label: 'Deactivation', value: 'Deactivation' },
        { label: 'RepairsWithoutInspection', value: 'RepairsWithoutInspection' },
        { label: 'Refurbishment', value: 'Refurbishment' },
      ],
      required: true,
    },
    {
      field: 'Impact',
      label: 'genericcomponent.technicalPolicy.impactsTable.impact',
    },
    {
      field: 'ImpactPriorisation',
      label: 'genericcomponent.technicalPolicy.impactsTable.priorityImpact',
      inputType: 'checkbox',
    },
    {
      field: 'FamilleEquipementRetrofit',
      label: 'genericcomponent.technicalPolicy.impactsTable.retrofitFamily',
      inputType: 'select',
      options: [{ label: 'Aucune', value: '' }, ...(family ?? [])],
    },
  ],
  GestionActivites: [
    'ReglesGestion',
    'CriteresDeclenchement',
    'Impacts',
    'Gisement',
    'ClasseActif',
    'id',
    'SequencesActionsImposees',
    'CoutsEtDureesActions',
    'SequencesActions',
  ],
  SequencesActionsImposees: [
    'DateRealisation',
    'NiveauDePriorisation',
    'Groupe',
    'Identifiant',
    'SequenceActions',
    'CategoriesBudgetaires',
  ],
  RequiredSequencesActionsImposees: ['DateRealisation', 'Groupe', 'Identifiant', 'SequenceActions'],
};
